import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrgAuth } from '../contexts/OrgAuthContext'
import Button from '../components/ui/Button'
import MoroccanLoader from '../components/MoroccanLoader'
import { orgAppClient, OrgAppClientError } from '../services/orgAppClient'

type FailureReason = 'used' | 'expired' | 'invalid' | 'rate_limited'

/**
 * Confirm page for one-time email login links (the email link lands here).
 * The GET/validation never burns the token — only the explicit confirm below
 * does, so email-scanner prefetches cannot invalidate the link.
 */
export default function MagicLinkPage() {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { refreshSession } = useOrgAuth()

  const [checking, setChecking] = useState(true)
  const [valid, setValid] = useState(false)
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null)
  const [expiresIn, setExpiresIn] = useState<number | null>(null)
  const [ttlMinutes, setTtlMinutes] = useState(15)
  const [failure, setFailure] = useState<FailureReason | null>(null)
  const [claiming, setClaiming] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!token) {
      setFailure('invalid')
      setChecking(false)
      return
    }
    orgAppClient
      .validateMagicLink(token)
      .then((data) => {
        if (cancelled) return
        const result = data as {
          valid: boolean
          masked_email: string | null
          expires_in: number | null
          reason: string | null
          expires_in_minutes?: number
        }
        setTtlMinutes(result.expires_in_minutes ?? 15)
        if (result.valid) {
          setValid(true)
          setMaskedEmail(result.masked_email)
          setExpiresIn(result.expires_in)
        } else {
          setFailure((result.reason as FailureReason) || 'invalid')
        }
      })
      .catch(() => {
        if (!cancelled) setFailure('invalid')
      })
      .finally(() => {
        if (!cancelled) setChecking(false)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const claimRedirect = () => {
    const redirect = searchParams.get('redirect')
    if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) return redirect
    return '/app'
  }

  const handleConfirm = async () => {
    if (!token) return
    setClaiming(true)
    try {
      await orgAppClient.claimMagicLink(token)
      await refreshSession()
      navigate(claimRedirect(), { replace: true })
    } catch (err) {
      const code = (err as OrgAppClientError).code
      if (code === 'RATE_LIMITED') setFailure('rate_limited')
      else if (code === 'MAGIC_LINK_USED') setFailure('used')
      else if (code === 'MAGIC_LINK_EXPIRED') setFailure('expired')
      else setFailure('invalid')
    } finally {
      setClaiming(false)
    }
  }

  const minutes = Math.max(1, Math.ceil((expiresIn ?? 0) / 60))

  return (
    <div className="min-h-[calc(100vh-9rem)] flex items-center justify-center bg-slate-50">
      <div className="w-full max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-xl bg-primary-100 flex items-center justify-center">
              <img src="/logo.svg" alt="Logo" className="h-8 w-8" />
            </div>
            <div>
              <p className="text-sm text-slate-600">{t('login.erpAccess')}</p>
              <h1 className="text-xl font-semibold text-slate-900">{t('magicPage.title')}</h1>
            </div>
          </div>

          {checking && (
            <MoroccanLoader message={t('common.loading', 'Loading...')} />
          )}

          {!checking && valid && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                {t('magicPage.requestedFor')} <strong>{maskedEmail}</strong>
              </p>

              <Button
                type="button"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={claiming}
                onClick={() => void handleConfirm()}
              >
                {claiming ? t('magicPage.signingIn') : t('magicPage.confirm')}
              </Button>

              <p className="text-xs text-slate-500 text-center">
                {t('magicPage.expiresMinutes', { minutes })}
              </p>
              <p className="text-xs text-slate-500 text-center">{t('magicPage.notYou')}</p>
            </div>
          )}

          {!checking && failure && (
            <div className="space-y-4" role="alert">
              <p className="text-base font-semibold text-slate-900">
                {failure === 'rate_limited'
                  ? t('magicPage.error_RATE_LIMITED')
                  : t(`magicPage.${failure}Title`)}
              </p>
              {failure !== 'rate_limited' && (
                <p className="text-sm text-slate-600">
                  {failure === 'expired'
                    ? t('magicPage.expiredBody', { minutes: ttlMinutes })
                    : t(`magicPage.${failure}Body`)}
                </p>
              )}
              <Button
                type="button"
                variant="primary"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                {t('magicPage.requestNew')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
